import {
  Component,
  DestroyRef,
  OnInit,
  OnDestroy,
  PipeTransform,
  ChangeDetectorRef,
  HostListener,
  inject,
} from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { startWith, tap } from 'rxjs/operators';

import { StudentService } from '../../services/student.service';
import { Student } from '../../interfaces/student.interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router,RouterLink, } from '@angular/router';
import { VolunteerService } from '../../services/volunteer.service';
import { PaginationComponent } from '../ui/pagination/pagination.component';
import { InactivateVolunteerModalComponent } from '../shared/inactivate-volunteer-modal/inactivate-volunteer-modal.component';
import {
  mapStudentToVolunteerListItem,
  mapVolunteerToListItem,
} from '../../mappers/volunteer.mapper';
import { VolunteerListItem } from '../../interfaces/volunteer.interfaces';
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'app-student',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    PaginationComponent,
    InactivateVolunteerModalComponent,
  ],
  templateUrl: './student.component.html',
  styleUrls: ['./student.component.scss'],
  providers: [DecimalPipe],
})
export class StudentComponent implements OnInit, OnDestroy {
  private apiService = inject(StudentService);
  private volunteerService = inject(
    VolunteerService,
  );
  private pipe = inject(DecimalPipe);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  data: Student[] = [];
  filteredData: Student[] = [];
  volunteerList: VolunteerListItem[] = [];
  filteredVolunteerList: VolunteerListItem[] = [];
  filter = new FormControl('', { nonNullable: true });

  acceptingId: string | null = null;
  actionLoadingId: string | null = null;
  actionMessage: string | null = null;
  showPendingOnly: boolean = false;

  inactivatingTarget: {
    id: string;
    folio: string;
    name: string;
    role: 'student' | 'volunteer';
  } | null = null;

  activeTab: 'ss' | 'general' = 'ss';

  // PAGINACIÓN INDEPENDIENTE POR PESTAÑA
  ssPage = 1;
  ssPageSize = 8;
  generalPage = 1;
  generalPageSize = 8;

  get socialServiceVolunteers(): VolunteerListItem[] {
    const list = this.filteredVolunteerList.filter(
      (v) => v.volunteerType === 'SERVICE_SOCIAL',
    );
    return this.sortWithInactivesAtEnd(list);
  }

  get generalVolunteers(): VolunteerListItem[] {
    const list = this.filteredVolunteerList.filter(
      (v) => v.volunteerType === 'GENERAL',
    );
    return this.sortWithInactivesAtEnd(list);
  }

  /**
   * Ordena la lista colocando los colaboradores inactivos y rechazados al final de la tabla.
   */
  private sortWithInactivesAtEnd(list: VolunteerListItem[]): VolunteerListItem[] {
    return [...list].sort((a, b) => {
      const aInactive = (a.accountStatus === 'inactive' || a.accountStatus === 'rejected') ? 1 : 0;
      const bInactive = (b.accountStatus === 'inactive' || b.accountStatus === 'rejected') ? 1 : 0;
      if (aInactive !== bInactive) {
        return aInactive - bInactive;
      }
      return 0;
    });
  }

  get pagedSocialServiceVolunteers(): VolunteerListItem[] {
    const start = (this.ssPage - 1) * this.ssPageSize;
    return this.socialServiceVolunteers.slice(start, start + this.ssPageSize);
  }

  get pagedGeneralVolunteers(): VolunteerListItem[] {
    const start = (this.generalPage - 1) * this.generalPageSize;
    return this.generalVolunteers.slice(start, start + this.generalPageSize);
  }

  isInviteDropdownOpen = false;
  copiedKey: string | null = null;
  private copyResetTimer?: ReturnType<typeof setTimeout>;

  get pendingStudentsCount(): number {
    return this.volunteerList.filter(
      (v) => v.volunteerType === 'SERVICE_SOCIAL' && v.accountStatus === 'pending',
    ).length;
  }

  // Conteos por pestaña: cuentan la MISMA lista visible (filteredVolunteerList)
  // filtrada por tipo, igual que el @if .some(...) de cada sección.
  get socialServiceCount(): number {
    return this.filteredVolunteerList.filter(
      (v) => v.volunteerType === 'SERVICE_SOCIAL',
    ).length;
  }

  get generalVolunteerCount(): number {
    return this.filteredVolunteerList.filter(
      (v) => v.volunteerType === 'GENERAL',
    ).length;
  }

  get pendingGeneralCount(): number {
    return this.volunteerList.filter(
      (v) => v.volunteerType === 'GENERAL' && v.accountStatus === 'pending',
    ).length;
  }

  get totalPendingCount(): number {
    return this.volunteerList.filter(
      (v) => v.accountStatus === 'pending',
    ).length;
  }

  private readonly _destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    forkJoin({
      students: this.apiService.getAllStudents(),
      volunteers: this.volunteerService.getAllVolunteers(),
    })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: ({
          students,
          volunteers,
        }) => {
          const studentVolunteers =
            students.map(
              mapStudentToVolunteerListItem,
            );
    
          const generalVolunteers =
            volunteers.map(
              mapVolunteerToListItem,
            );
    
          this.volunteerList = [
            ...studentVolunteers,
            ...generalVolunteers,
          ].sort((a, b) => {
            const nameA = (a.fullName || a.mail || a.folio || '').trim();
            const nameB = (b.fullName || b.mail || b.folio || '').trim();
            return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
          });

          this.applyFilters();

          console.log(
            '🟢 SERVICIO SOCIAL:',
            studentVolunteers,
          );

          console.log(
            '🟢 VOLUNTARIOS GENERALES:',
            generalVolunteers,
          );

          console.log(
            '🔥 LISTA UNIFICADA:',
            this.volunteerList,
          );
        },

        error: (error) => {
          console.error(
            '❌ ERROR CARGANDO VOLUNTARIOS:',
            error,
          );
        },
      });

    this.filter.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        startWith(''),
        tap(() => {
          this.applyFilters();
        }),
      )
      .subscribe();
  }

  applyFilters(): void {
    const searchTerm = (this.filter.value ?? '').toString().toLowerCase().trim();

    let list = this.volunteerList;

    if (this.showPendingOnly) {
      list = list.filter((v) => v.accountStatus === 'pending');
    }

    if (searchTerm) {
      list = list.filter((volunteer) => {
        return [
          volunteer.folio,
          volunteer.fullName,
          volunteer.career,
          volunteer.mail,
          volunteer.phone,
          volunteer.school,
          volunteer.matricula,
        ]
          .filter(Boolean)
          .some((value) =>
            value!.toString().toLowerCase().includes(searchTerm),
          );
      });
    }

    this.filteredVolunteerList = list;
    this.ssPage = 1;
    this.generalPage = 1;
    this.cdr.detectChanges();
  }

  setTab(tab: 'ss' | 'general'): void {
    this.activeTab = tab;
  }

  togglePendingFilter(): void {
    this.showPendingOnly = !this.showPendingOnly;
    this.applyFilters();
  }

  setPendingFilter(pendingOnly: boolean): void {
    this.showPendingOnly = pendingOnly;
    this.applyFilters();
  }

  private totalValidatedHours(student: Student): number {
    let res = 0;
    for (const hours of student.assignedActivities) {
      res += hours.validatedHours || 0;
    }
    return res;
  }

  private search(text: string, pipe: PipeTransform): Student[] {
    const term = text.toLowerCase();
    return this.data.filter((student) => {
      return (
        student.fullName.toLowerCase().includes(term) ||
        student.career.toLowerCase().includes(term) ||
        student.mail.toLowerCase().includes(term) ||
        student.schoolMail.toLowerCase().includes(term) ||
        student.phone.includes(term) ||
        pipe.transform(this.totalValidatedHours(student)).includes(term)
      );
    });
  }

  viewStudent(studentId: string): void {
    this.router.navigate(['/student', studentId]);
  }

  acceptVolunteerItem(volunteer: VolunteerListItem, event: Event): void {
    event.stopPropagation();
    if (this.actionLoadingId) return;

    const comments = window.prompt(
      `Mensaje o nota de bienvenida para ${volunteer.fullName || volunteer.folio} (se incluirá en el correo oficial y en su expediente):`,
      ''
    );
    if (comments === null) {
      // El administrador canceló la aceptación
      return;
    }

    this.actionLoadingId = volunteer.id;
    this.actionMessage = null;

    const req$: Observable<unknown> =
      volunteer.volunteerType === 'SERVICE_SOCIAL'
        ? this.apiService.acceptStudent(volunteer.id, comments.trim())
        : this.volunteerService.acceptVolunteer(volunteer.id, comments.trim());

    req$.subscribe({
      next: () => {
        this.actionLoadingId = null;
        this.actionMessage =
          volunteer.volunteerType === 'SERVICE_SOCIAL'
            ? 'Estudiante aceptado exitosamente. Se ha emitido su carta oficial y enviado su notificación.'
            : 'Voluntario general aceptado exitosamente. Se ha emitido su constancia y notificado a su cuenta.';

        this.volunteerList = this.volunteerList.map((v) =>
          v.id === volunteer.id ? { ...v, accountStatus: 'active' as const } : v,
        );
        this.applyFilters();

        setTimeout(() => {
          this.actionMessage = null;
          this.cdr.detectChanges();
        }, 5000);
      },
      error: (err: unknown) => {
        this.actionLoadingId = null;
        console.error('Error al aceptar voluntario:', err);
        this.actionMessage = 'Error al aceptar. Por favor intenta de nuevo.';
        this.cdr.detectChanges();
      },
    });
  }

  rejectVolunteerItem(volunteer: VolunteerListItem, event: Event): void {
    event.stopPropagation();
    if (this.actionLoadingId) return;

    const comments = window.prompt(
      `Motivo u observaciones para el rechazo de la solicitud de ${volunteer.fullName || volunteer.folio}:`,
      'Documentación incompleta o no legible. Favor de corregir en el registro.'
    );
    if (comments === null) return;

    this.actionLoadingId = volunteer.id;
    this.actionMessage = null;

    const req$: Observable<unknown> =
      volunteer.volunteerType === 'SERVICE_SOCIAL'
        ? this.apiService.rejectStudent(volunteer.id, comments.trim())
        : this.volunteerService.rejectVolunteer(volunteer.id, comments.trim());

    req$.subscribe({
      next: () => {
        this.actionLoadingId = null;
        this.actionMessage = 'Solicitud de ingreso rechazada y notificada por correo.';

        this.volunteerList = this.volunteerList.map((v) =>
          v.id === volunteer.id ? { ...v, accountStatus: 'rejected' as const } : v,
        );
        this.applyFilters();

        setTimeout(() => {
          this.actionMessage = null;
          this.cdr.detectChanges();
        }, 5000);
      },
      error: (err: unknown) => {
        this.actionLoadingId = null;
        console.error('Error al rechazar voluntario:', err);
        this.actionMessage = 'Error al rechazar. Por favor intenta de nuevo.';
        this.cdr.detectChanges();
      },
    });
  }

  openInactivateModal(volunteer: VolunteerListItem, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.inactivatingTarget = {
      id: volunteer.id,
      folio: volunteer.folio || '',
      name: volunteer.fullName || volunteer.folio || 'Colaborador',
      role: volunteer.volunteerType === 'SERVICE_SOCIAL' ? 'student' : 'volunteer',
    };
  }

  closeInactivateModal(): void {
    this.inactivatingTarget = null;
  }

  handleInactivateConfirmed(event: { comments: string }): void {
    if (!this.inactivatingTarget) return;

    const target = this.inactivatingTarget;
    this.actionLoadingId = target.id;
    this.actionMessage = null;

    const req$: Observable<unknown> =
      target.role === 'student'
        ? this.apiService.updateStudentAccountStatus(
            target.id,
            'inactive',
            'administrative_decision',
            event.comments,
          )
        : this.volunteerService.updateVolunteerAccountStatus(
            target.id,
            'inactive',
            'administrative_decision',
            event.comments,
          );

    req$.subscribe({
      next: () => {
        this.actionLoadingId = null;
        this.actionMessage = `Cuenta de ${target.name} dada de baja exitosamente por decisión administrativa.`;
        this.volunteerList = this.volunteerList.map((v) =>
          v.id === target.id ? { ...v, accountStatus: 'inactive' as const } : v,
        );
        this.closeInactivateModal();
        this.applyFilters();

        setTimeout(() => {
          this.actionMessage = null;
          this.cdr.detectChanges();
        }, 5000);
      },
      error: (err: unknown) => {
        this.actionLoadingId = null;
        console.error('Error al inactivar colaborador:', err);
        this.actionMessage = 'Error al inactivar la cuenta. Por favor intenta de nuevo.';
        this.closeInactivateModal();
        this.cdr.detectChanges();
      },
    });
  }

  acceptStudent(id: string, event: Event): void {
    const item = this.volunteerList.find((v) => v.id === id);
    if (item) {
      this.acceptVolunteerItem(item, event);
    } else {
      this.acceptVolunteerItem(
        { id, volunteerType: 'SERVICE_SOCIAL' } as any,
        event,
      );
    }
  }

  rejectStudent(id: string, event: Event): void {
    const item = this.volunteerList.find((v) => v.id === id);
    if (item) {
      this.rejectVolunteerItem(item, event);
    } else {
      this.rejectVolunteerItem(
        { id, volunteerType: 'SERVICE_SOCIAL' } as any,
        event,
      );
    }
  }

  toggleInviteDropdown(): void {
    this.isInviteDropdownOpen = !this.isInviteDropdownOpen;
    this.cdr.detectChanges();
  }

  closeInviteDropdown(): void {
    if (this.isInviteDropdownOpen) {
      this.isInviteDropdownOpen = false;
      this.cdr.detectChanges();
    }
  }

  getFullUrl(path: string): string {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://abaq.org.mx';
    return `${origin}${path}`;
  }

  copyInviteLink(key: 'general' | 'vg' | 'ss' | 'login'): void {
    let path = '/auth/signup';

    switch (key) {
      case 'general':
        path = '/auth/signup';
        break;
      case 'vg':
        path = '/auth/signup?type=VG';
        break;
      case 'ss':
        path = '/auth/signup?type=SS';
        break;
      case 'login':
        path = '/auth/login';
        break;
    }

    const fullUrl = this.getFullUrl(path);

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(fullUrl)
        .then(() => {
          this.copiedKey = key;
          this.cdr.detectChanges();

          if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
          this.copyResetTimer = setTimeout(() => {
            this.copiedKey = null;
            this.cdr.detectChanges();
          }, 2500);
        })
        .catch(() => {
          // Si el portapapeles no tiene permisos, no mostrar toast
        });
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isInviteDropdownOpen) {
      const target = event.target as HTMLElement;
      if (!target.closest('.invite-dropdown-wrap')) {
        this.closeInviteDropdown();
      }
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeInviteDropdown();
  }

  ngOnDestroy(): void {
    if (this.copyResetTimer) {
      clearTimeout(this.copyResetTimer);
    }
  }
}
